import axios from "axios";

export enum ConversionActionPlatform {
  GOOGLE_ADS = "google-ads",
  META = "facebook",
}

export enum ConversionActionTarget {
  BINARY = "binary",
  SCALED = "scaled",
  VALUE = "value",
}

export interface ConversionAction {
  id: number;
  "workspace-id": number;
  "integration-id": number;
  platform: ConversionActionPlatform;
  name: string;
  target: ConversionActionTarget;
  "binary-percentile": number;
  "binary-below?": boolean;
  "google-ads-account": null | string;
  "google-ads-login-customer-id": null | string;
  "google-ads-id": null | string;
  "facebook-ad-account": null | string;
  "dataset-id": string;
  "recent-events": number;
  "last-sent-at": string | null;
  "created-at": string;
  "updated-at": string;
}

export const getConversionActions = async (
  workspaceSlug: string,
  platform?: ConversionActionPlatform,
): Promise<ConversionAction[]> => {
  return (
    await axios.get(`/api/v1/workspaces/${workspaceSlug}/conversion-actions`, {
      params: platform ? { platform } : undefined,
    })
  ).data;
};

export interface CreateConversionActionRequest {
  platform: ConversionActionPlatform;
  name: string;
  target: ConversionActionTarget;
  "binary-percentile": number;
  "binary-below?": boolean;
  "google-ads-account": null | string;
  "google-ads-login-customer-id": null | string;
  "google-ads-id": null | string;
  "facebook-ad-account": null | string;
  "dataset-id": null | string;
  "access-token": null | string;
}

export const createConversionAction = async (
  workspaceSlug: string,
  data: CreateConversionActionRequest,
): Promise<ConversionAction> => {
  return (
    await axios.post(
      `/api/v1/workspaces/${workspaceSlug}/conversion-actions`,
      data,
    )
  ).data;
};

export const deleteConversionAction = async (
  workspaceSlug: string,
  actionId: number,
): Promise<void> => {
  await axios.delete(
    `/api/v1/workspaces/${workspaceSlug}/conversion-actions/${actionId}`,
  );
};

export interface ConversionActionTemplate {
  name: string;
  description: string;
}

export const getConversionActionTemplates = async (
  workspaceSlug: string,
): Promise<ConversionActionTemplate[]> => {
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/conversion-actions/templates`,
    )
  ).data;
};

export interface ApplyConversionActionTemplatesRequest {
  "dataset-id": string;
  "access-token": string;
  "max-conversions-percentile": number;
}

export const applyConversionActionTemplates = async (
  workspaceSlug: string,
  data: ApplyConversionActionTemplatesRequest,
): Promise<ConversionAction[]> => {
  return (
    await axios.post(
      `/api/v1/workspaces/${workspaceSlug}/conversion-actions/templates`,
      data,
    )
  ).data;
};

export interface ConversionActionSyncedEvent {
  id: number;
  "conversion-action-id": number;
  fbclid: string | null;
  fbc: string | null;
  gclid: string | null;
  value: number;
  result: unknown | null;
  "error-code": string | null;
  "error-message": string | null;
  "created-at": string;
}

export interface ConversionActionSyncedEventsPage {
  items: ConversionActionSyncedEvent[];
  meta: {
    total: number;
  };
}

export const getConversionActionSyncedEvents = async (
  workspaceSlug: string,
  actionId: number,
  { days, limit }: { days?: number; limit?: number } = {},
): Promise<ConversionActionSyncedEventsPage> => {
  const params = new URLSearchParams();
  if (days !== undefined) {
    params.set("days", String(days));
  }
  if (limit !== undefined) {
    params.set("limit", String(limit));
  }
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/conversion-actions/${actionId}/synced-events`,
      { params },
    )
  ).data;
};
